import { callInternal } from './internal.js';
import { normalizeAssetUrl } from '../utils/asset-url.js';
import { resolveExternalImageUrl } from '../utils/media-url.js';

const SERVICE = 'message';

export type HomeMessageType = 'system' | 'event' | 'examine' | 'shop_inspection';

export const HOME_MESSAGE_TYPES: HomeMessageType[] = ['system', 'event', 'examine', 'shop_inspection'];

export interface MessageRelation {
	examine_id?: string | number;
	event_id?: string | number;
	plan_id?: string | number;
	pic_id?: string | number;
	store_id?: string | number;
	store?: string;
	store_name?: string;
	device_id?: string | number;
	device_auto_id?: string | number;
	device_sn?: string | number;
	device_name?: string;
	channel?: string | number;
	channel_id?: string | number;
	pic_path?: string;
	pic_url?: string;
	img_url?: string;
	image_url?: string;
	thumbnail?: string;
	[k: string]: unknown;
}

export interface MessageItem {
	message_id?: string;
	type?: string;
	title?: string;
	text?: string;
	format_text?: string;
	create_time?: string;
	current_cursor?: string;
	next_cursor?: string;
	stat?: string;
	unread_count?: string | number;
	widget_id?: string;
	images?: string[];
	relation?: MessageRelation | string;
	[k: string]: unknown;
}

export interface MessageCursorResult {
	items: MessageItem[];
	nextCursor: string;
}

export interface MessageUnreadItem {
	title?: string;
	type?: string;
	unread_count?: string | number;
	[k: string]: unknown;
}

export interface StoreInspectionMessageTarget {
	messageId?: string;
	picId?: string;
	planId?: string;
	storeId?: string;
	storeName?: string;
	deviceAutoId?: string;
	deviceSn?: string;
	deviceName?: string;
	channelId?: string;
	imageUrl?: string;
	imageUrlRaw?: string;
	imageFallbackUrl?: string;
	createdAt?: string;
	title?: string;
}

interface MessageCursorResponse {
	items?: MessageItem[];
	next_cursor?: string;
}

function itemsFrom(data: unknown): MessageItem[] {
	if (Array.isArray(data)) return data as MessageItem[];
	if (!data || typeof data !== 'object') return [];
	const record = data as Record<string, unknown>;
	if (Array.isArray(record.items)) return record.items as MessageItem[];
	if (Array.isArray(record.data)) return record.data as MessageItem[];
	if (Array.isArray(record.list)) return record.list as MessageItem[];
	return [];
}

function unreadItemsFrom(data: unknown): MessageUnreadItem[] {
	if (Array.isArray(data)) return data as MessageUnreadItem[];
	if (!data || typeof data !== 'object') return [];
	const record = data as Record<string, unknown>;
	if (Array.isArray(record.items)) return record.items as MessageUnreadItem[];
	if (Array.isArray(record.data)) return record.data as MessageUnreadItem[];
	if (Array.isArray(record.list)) return record.list as MessageUnreadItem[];
	return [];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
	return value && typeof value === 'object' ? value as Record<string, unknown> : undefined;
}

function parseMessageRelation(message: MessageItem): MessageRelation | undefined {
	const rawRelation = message.relation;
	if (rawRelation && typeof rawRelation === 'object') return rawRelation;
	if (typeof rawRelation !== 'string' || !rawRelation.trim()) return undefined;
	try {
		const parsed = JSON.parse(rawRelation);
		return parsed && typeof parsed === 'object' ? parsed as MessageRelation : undefined;
	} catch {
		return undefined;
	}
}

function firstText(record: Record<string, unknown> | undefined, keys: string[]): string {
	if (!record) return '';
	for (const key of keys) {
		const value = record[key];
		if (value === undefined || value === null) continue;
		const text = String(value).trim();
		if (text) return text;
	}
	return '';
}

function firstUrl(value: unknown): string {
	if (!value) return '';
	if (typeof value === 'string') {
		return normalizeAssetUrl(value);
	}
	if (Array.isArray(value)) {
		for (const item of value) {
			const url = firstUrl(item);
			if (url) return url;
		}
		return '';
	}
	const record = asRecord(value);
	if (!record) return '';
	return normalizeAssetUrl(firstText(record, [
		'pic_path',
		'pic_url',
		'img_url',
		'image_url',
		'thumbnail',
		'thumb',
		'cover',
		'url',
		'path',
		'src',
	]));
}

function firstHtmlImageUrl(value: string): string {
	const match = /<img[^>]+src=["']([^"']+)["']/i.exec(value);
	if (!match) return '';
	return firstUrl(match[1]);
}

function capturedStoreDevice(text: string): { storeName: string; deviceName: string } {
	const normalized = text.replace(/\s+/g, ' ').trim();
	const english = /captured by device\s+(.+?)\s+in store\s+(.+)$/i.exec(normalized);
	if (english) {
		return {
			deviceName: english[1].trim(),
			storeName: english[2].trim(),
		};
	}
	return { storeName: '', deviceName: '' };
}

export async function fetchMessageUnreadCounts(): Promise<MessageUnreadItem[]> {
	const res = await callInternal<MessageUnreadItem[] | { items?: MessageUnreadItem[] }>({
		short_name: SERVICE,
		action: '/user/message/unread_count',
		method: 'GET',
		av: '4',
	});
	if (res.code !== 0) return [];
	return unreadItemsFrom(res.data);
}

export async function fetchMessageCursor(opts: {
	type: HomeMessageType | string;
	cursor?: string;
	count?: number;
}): Promise<MessageCursorResult> {
	const res = await callInternal<MessageCursorResponse | MessageItem[]>({
		short_name: SERVICE,
		action: '/user/message/cursor',
		method: 'GET',
		query: {
			cursor: opts.cursor ?? '',
			count: opts.count ?? 20,
			title: opts.type,
		},
		av: '4',
	});
	if (res.code !== 0) return { items: [], nextCursor: '' };
	const data = res.data as unknown;
	const items = itemsFrom(data);
	const nextCursor = data && typeof data === 'object'
		? String((data as MessageCursorResponse).next_cursor ?? '')
		: '';
	return { items, nextCursor };
}

export async function markMessageRead(message: MessageItem): Promise<void> {
	if (!message.message_id) return;
	await callInternal({
		short_name: SERVICE,
		action: '/user/message/updateState',
		method: 'POST',
		body: {
			message_id: message.message_id,
			sub_type: '1',
			widget_id: message.widget_id ?? '',
		},
		av: '2',
	});
}

export function messageUnreadCount(items: MessageUnreadItem[], type: HomeMessageType): number {
	for (const item of items) {
		const key = String(item.title ?? item.type ?? '');
		if (key !== type) continue;
		const count = Number(item.unread_count ?? 0);
		return Number.isFinite(count) ? count : 0;
	}
	return 0;
}

export function stripMessageHtml(value: string): string {
	if (!value) return '';
	return value
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.trim();
}

export function messagePlainText(message: MessageItem): string {
	const formatted = String(message.format_text ?? '').trim();
	if (formatted) return formatted;
	return stripMessageHtml(String(message.text ?? ''));
}

export function messageTimestamp(message: MessageItem): number {
	const value = String(message.create_time ?? '').trim();
	if (!value) return NaN;
	if (/^\d+$/.test(value)) {
		const parsed = Number(value);
		return parsed < 100000000000 ? parsed * 1000 : parsed;
	}
	const normalized = value.replace(/-/g, '/');
	const timestamp = new Date(normalized).getTime();
	return Number.isFinite(timestamp) ? timestamp : NaN;
}

export function pickLatestMessage(messages: MessageItem[]): MessageItem | null {
	let latest: MessageItem | null = null;
	let latestTime = -Infinity;
	for (const message of messages) {
		const time = messageTimestamp(message);
		const comparableTime = Number.isFinite(time) ? time : -Infinity;
		if (!latest || comparableTime > latestTime) {
			latest = message;
			latestTime = comparableTime;
		}
	}
	return latest;
}

export function extractAssessmentMessageExamineId(message: MessageItem): string {
	const relation = parseMessageRelation(message);
	const relationId = relation?.examine_id;
	if (relationId !== undefined && relationId !== null && String(relationId).trim()) {
		return String(relationId).trim();
	}
	const text = String(message.text ?? '');
	const match = /[?&]examine_id=([^"'&<>\s]+)/.exec(text);
	if (!match) return '';
	try {
		return decodeURIComponent(match[1]);
	} catch {
		return match[1];
	}
}

export function extractEventMessageEventId(message: MessageItem): string {
	const relation = parseMessageRelation(message);
	const relationId = relation?.event_id;
	if (relationId !== undefined && relationId !== null && String(relationId).trim()) {
		return String(relationId).trim();
	}
	const topLevelId = (message as Record<string, unknown>).event_id;
	if (topLevelId !== undefined && topLevelId !== null && String(topLevelId).trim()) {
		return String(topLevelId).trim();
	}
	const text = String(message.text ?? '');
	const queryMatch = /[?&]event_id=([^"'&<>\s]+)/.exec(text);
	const pathMatch = /\/(?:m\/home\/events\/detail|event(?:s)?\/detail)\/([^"'?&<>\s/]+)/.exec(text);
	const rawId = queryMatch?.[1] ?? pathMatch?.[1] ?? '';
	if (!rawId) return '';
	try {
		return decodeURIComponent(rawId);
	} catch {
		return rawId;
	}
}

export function extractStoreInspectionMessageTarget(message: MessageItem): StoreInspectionMessageTarget | null {
	const relation = parseMessageRelation(message);
	const messageRecord = message as Record<string, unknown>;
	const relationRecord = asRecord(relation);
	const text = messagePlainText(message);
	const parsedText = capturedStoreDevice(text);
	const messageId = firstText(messageRecord, ['message_id', 'current_cursor']);
	const storeName = firstText(relationRecord, ['store_name', 'store', 'group_name', 'shop_name'])
		|| firstText(messageRecord, ['store_name', 'store', 'group_name', 'shop_name'])
		|| parsedText.storeName;
	const deviceName = firstText(relationRecord, ['device_name', 'device_alias', 'alias', 'name', 'device_id', 'device_sn', 'sn'])
		|| firstText(messageRecord, ['device_name', 'device_alias', 'alias', 'name', 'device_id', 'device_sn', 'sn'])
		|| parsedText.deviceName;
	const imageSource = firstUrl(message.images)
		|| firstUrl(relation)
		|| firstUrl(messageRecord)
		|| firstHtmlImageUrl(String(message.text ?? ''));
	const image = resolveExternalImageUrl(imageSource);
	const target: StoreInspectionMessageTarget = {
		messageId,
		picId: firstText(relationRecord, ['pic_id', 'picture_id', 'screenshot_id'])
			|| firstText(messageRecord, ['pic_id', 'picture_id', 'screenshot_id']),
		planId: firstText(relationRecord, ['plan_id', 'task_id'])
			|| firstText(messageRecord, ['plan_id', 'task_id']),
		storeId: firstText(relationRecord, ['store_id'])
			|| firstText(messageRecord, ['store_id']),
		deviceAutoId: firstText(relationRecord, ['device_auto_id'])
			|| firstText(messageRecord, ['device_auto_id']),
		storeName,
		deviceSn: firstText(relationRecord, ['device_sn', 'sn', 'device_serial', 'device_id'])
			|| firstText(messageRecord, ['device_sn', 'sn', 'device_serial', 'device_id']),
		deviceName,
		channelId: firstText(relationRecord, ['channel_id', 'channel'])
			|| firstText(messageRecord, ['channel_id', 'channel']),
		imageUrl: image.primary,
		imageUrlRaw: image.raw,
		imageFallbackUrl: image.fallback,
		createdAt: String(message.create_time ?? '').trim(),
		title: '',
	};
	const right = target.deviceName || target.deviceSn;
	if (target.storeName || right) {
		target.title = `${target.storeName || '--'}/${right || '--'}`;
	} else {
		target.title = text || 'Store Inspection';
	}
	if (!target.messageId && !target.picId && !target.planId && !target.storeId && !target.imageUrl && !text) {
		return null;
	}
	return target;
}
